/**
 * ChatFilePickerModal — 채널 최근 메시지에서 첨부파일을 골라 게시글/댓글 첨부로 가져오기.
 *
 * 원래 CommentSection.tsx 안에 정의되어 있던 것을 별도 파일로 추출 — PostCreateModal /
 * PostDetailOverlay 의 본문 첨부에서도 동일 컴포넌트를 재사용하기 위함.
 */
import React, { useEffect, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { Loader2, FileText } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from '../../components/ui/use-toast';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { CommentAttachment } from '../../models/Board';

interface ChatFile {
  url: string;
  filename: string;
  sizeByte: number;
  group: 'image' | 'doc';
  messageId: string;
  authorName: string;
  createdAt: string;
}

interface Props {
  open: boolean;
  channelId: string;
  onClose: () => void;
  onPick: (att: CommentAttachment) => void;
}

export const ChatFilePickerModal: React.FC<Props> = ({ open, channelId, onClose, onPick }) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<ChatFile[]>([]);

  useEffect(() => {
    if (!open || !My.user?.id || !channelId) return;
    setLoading(true);
    Fire.msg.getMessages(My.user.id, channelId, null, 100)
      .then(msgs => {
        const collected: ChatFile[] = [];
        for (const m of msgs) {
          const attachedFiles = (m as any).files;
          if (!Array.isArray(attachedFiles) || attachedFiles.length === 0) continue;
          for (const f of attachedFiles) {
            if (!f?.url || !f?.filename) continue;
            collected.push({
              url: f.url,
              filename: f.filename,
              sizeByte: f.sizeByte ?? 0,
              group: f.group === 'image' ? 'image' : 'doc',
              messageId: m.id,
              authorName: m.user?.name ?? '',
              createdAt: (m as any).created ?? '',
            });
          }
        }
        setFiles(collected);
      })
      .catch(err => {
        console.error('[ChatFilePicker] 파일 목록 로드 실패:', err);
        toast({ title: t`Cannot load chat file list`, variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  }, [open, channelId]);

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[640px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle><Trans>Import files from chat</Trans></DialogTitle>
          <DialogDescription className="sr-only"><Trans>Select from attachments in recent channel messages</Trans></DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-2 px-2">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : files.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">
              <Trans>No attachments found in the last 100 messages of this channel.</Trans>
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {files.map((f, idx) => (
                <button
                  key={`${f.url}-${idx}`}
                  onClick={() => onPick({
                    filename: f.filename,
                    url: f.url,
                    sizeByte: f.sizeByte,
                    group: f.group,
                    source: 'chat',
                    sourceMessageId: f.messageId,
                  })}
                  className="flex flex-col gap-1 p-2 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/40 transition-colors text-left"
                  title={f.filename}
                >
                  {f.group === 'image' ? (
                    <img src={f.url} alt={f.filename} className="w-full h-24 rounded object-cover" />
                  ) : (
                    <div className="w-full h-24 rounded bg-muted/60 flex items-center justify-center">
                      <FileText className="size-8 text-muted-foreground/60" />
                    </div>
                  )}
                  <span className="text-xs font-medium truncate">{f.filename}</span>
                  <span className="text-meta-sm text-muted-foreground truncate">
                    {f.authorName} {f.createdAt && '· ' + new Date(f.createdAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChatFilePickerModal;
